import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import axios from 'axios';
import { auth, getIdTokenSafe } from './firebase';
import { signOut } from 'firebase/auth';

// Configure axios defaults for API Gateway
axios.defaults.baseURL =
  process.env.REACT_APP_API_BASE_URL || 'https://uat.sniffrai.com';
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Attach Firebase ID token
axios.interceptors.request.use(
  async (config) => {
    const idToken = await getIdTokenSafe();
    // Always overwrite Authorization if we have a token
    if (idToken) {
      config.headers.Authorization = `Bearer ${idToken}`;
    } else {
      // Ensure header is not left as "Bearer null" by callers
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Central 401 handling: single retry with fresh token, then sign out
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;

    if (status === 401 && !originalRequest.__isRetryRequest) {
      originalRequest.__isRetryRequest = true;
      try {
        const freshToken = await getIdTokenSafe(true); // force-refresh expired token
        if (freshToken) {
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${freshToken}`,
          };
          return axios(originalRequest);
        }
      } catch (_) {}
      // Fallthrough to sign out below if retry not possible
    }

    if (status === 401) {
      try {
        await signOut(auth);
      } catch {}
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#E53950', // Diffly.AI Red
    },
    secondary: {
      main: '#4DC47D', // Diffly.AI Green
    },
    info: {
      main: '#3DB2FF', // Diffly.AI Blue
    },
    warning: {
      main: '#F9D923', // Diffly.AI Yellow
    },
    background: {
      default: '#f9f9f9',
    },
  },
  typography: {
    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
); 